/**
 * Клиентский раздел «Мои заказы» (orders-core). Список карточек заказов владельца (услуга, объём,
 * дата, бейдж состояния, текущий этап) + создание заказа (услуга + объём → POST черновика) и
 * действия: «Продолжить» (черновик), «Скачать» (готовые файлы), «Выгрузить в Excel», удаление
 * черновика, «Отменить заказ» и «Восстановить» (order-cancel-restore). Owner-scoping — на бэкенде
 * (клиент свой client_id не передаёт). Над списком — фильтры «Вид услуги / Статус / Этап»
 * (orders-list-filters; ТЗ п.8.1), они хранятся в адресе страницы.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { AlertTriangle, ClipboardList, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ecologyOrdersApi, ordersApi, type OrderCard, type Service } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  clientBadge,
  clientOrderActions,
  clientOrderTarget,
  compositionSummary,
  serviceAnketaPath,
  serviceLabel,
  SERVICE_LABELS,
} from '@/lib/orders'
import { hasIncompleteTrainingConsents, trainingConsentCounterLabel } from '@/lib/training'
import { exportOrderToExcel } from '@/lib/excel'
import { CancelOrderDialog, RestoreOrderDialog, cancelledByText } from '@/orders/order-cancel'
import { useTrainingOrderStart } from '@/training/training-order-start'
import { downloadFromUrl } from '@/lib/download'
import { PAGE_SIZE, pageCount } from '@/lib/pagination'
import {
  OrderListFilters,
  hasOrderListFilters,
  orderListFilterParams,
  orderListQuery,
  readOrderListFilters,
  type OrderListFilterValue,
} from '@/orders/order-list-filters'
import { Pagination } from '@/design-system/components/data/pagination'
import { useAuth } from '@/app/auth-context'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Услуги, доступные роли клиента: физлицо — только обучение (инвариант). «Экология» есть у компании:
 * её черновик создаётся отдельным эндпоинтом раздела (ecology-order), мастер — тот же путь анкеты.
 */
function servicesForRole(individual: boolean): Service[] {
  return individual ? ['training'] : ['sout', 'training', 'profrisk', 'ecology']
}

function CreateOrderForm({
  individual,
  onCreated,
}: {
  individual: boolean
  onCreated: (id: string, service: Service) => void
}) {
  const services = servicesForRole(individual)
  const [service, setService] = useState<Service>(services[0])
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Заказ обучения — с вопросом «Заполнить по сотрудникам с истекающим и просроченным
  // обучением?» (training-order-entry-points; ТЗ п.2.2).
  const trainingStart = useTrainingOrderStart(individual)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (service === 'training') {
      trainingStart.start()
      return
    }
    setSubmitting(true)
    try {
      // Объём вручную не вводим (D10) — размер заказа считается из анкеты услуги. Заказ по
      // экологии создаётся эндпоинтом раздела: у него свои строки (услуга и площадки).
      const order =
        service === 'ecology'
          ? await ecologyOrdersApi.create({})
          : await ordersApi.create({ service })
      onCreated(order.id, service)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось создать заказ'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card header="Новый заказ" className="mb-4">
      <form onSubmit={onSubmit} noValidate>
        {error || trainingStart.error ? (
          <Alert variant="danger" className="mb-4">
            {error ?? trainingStart.error}
          </Alert>
        ) : null}
        {trainingStart.dialog}
        <Field
          label="Услуга"
          required
          hint="Состав заказа заполните в анкете на следующем шаге"
        >
          {(props) => (
            <Select value={service} onValueChange={(v) => setService(v as Service)}>
              {/* Фиксированная ширина, а не на всю карточку (замечание заказчика): названия
                  услуг короткие, растянутое поле читается хуже. */}
              <SelectTrigger
                className="w-64 max-w-full"
                id={props.id}
                aria-label="Услуга заказа"
                aria-required={props['aria-required']}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {services.map((s) => (
                  <SelectItem key={s} value={s}>
                    {SERVICE_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Button type="submit" disabled={submitting || trainingStart.starting}>
          {submitting || trainingStart.starting ? 'Создание…' : 'Создать заказ'}
        </Button>
      </form>
    </Card>
  )
}

function OrderCardItem({
  order,
  onOpen,
  onDelete,
  onExport,
  onDownload,
  onCancel,
  onRestore,
  busy,
}: {
  order: OrderCard
  onOpen: () => void
  onDelete: () => void
  onExport: () => void
  onDownload: () => void
  onCancel: () => void
  onRestore: () => void
  busy: boolean
}) {
  const badge = clientBadge(order.dataStatus)
  const actions = clientOrderActions(order.dataStatus)
  // Объём заказа обучения в фазе данных — места заявки (training-listeners-count): по программе
  // можно указать только число слушателей, названных ещё нет; иначе — названные слушатели.
  const summaryCount =
    order.service === 'training' ? Math.max(order.itemCount, order.listenersTotal ?? 0) : order.itemCount
  // Заказ, возвращённый клиенту на правки (centrika-docs!98): красная рамка карточки и красный
  // треугольник с восклицательным знаком рядом с бейджем статуса. Выделение определяется только
  // текущим статусом — снимается после повторной отправки, когда список перечитан.
  const changesRequested = order.dataStatus === 'changes_requested'
  return (
    <Card
      className={changesRequested ? 'border-red' : undefined}
      data-changes-requested={changesRequested ? 'true' : undefined}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Номер заказа над наименованием услуги (centrika-docs!50) — вспомогательная
              строка, поэтому мельче и тусклее заголовка карточки. */}
          <p className="text-12 text-txt-tertiary">Заказ № {order.number}</p>
          <div className="flex items-center gap-2.5">
            <span className="font-semibold text-txt">{serviceLabel(order.service)}</span>
            <Badge status={badge.role}>{badge.label}</Badge>
            {changesRequested ? (
              <AlertTriangle
                className="size-4 shrink-0 stroke-current text-red"
                role="img"
                aria-label="Требуются правки"
              />
            ) : null}
          </div>
          <p className="mt-1 text-txt-secondary">
            {summaryCount > 0 ? compositionSummary(order.service, summaryCount) : 'Ещё не заполнено'}
          </p>
          {/* Отменённый заказ (order-cancel-restore): кем, когда и почему. */}
          {order.cancellation ? (
            <p className="mt-1 text-12 text-txt-secondary">
              {cancelledByText(order.cancellation, 'client')} {formatDate(order.cancellation.at)}
              {order.cancellation.reason ? ` · Причина: ${order.cancellation.reason}` : ''}
            </p>
          ) : null}
          {/* Неполный комплект согласий ПД у отправленного заказа обучения (centrika-docs,
              MR !36) — между сводкой состава и строкой этапа. Не врезка `Alert`, а строка в
              цвете предупреждения: врезка с рамкой на всю ширину читалась бы как ошибка
              заказа (см. design D6). */}
          {/* Изменения состава заявки на согласовании (training-change-approval): изменения Центрики
              ждут клиента — строкой предупреждения, свои — информационной. */}
          {order.pendingChange === 'awaiting_client' ? (
            <p className="mt-1 text-12 text-amber">
              Центрика изменила состав заявки — согласуйте или отклоните изменения
            </p>
          ) : order.pendingChange === 'awaiting_team' ? (
            <p className="mt-1 text-12 text-txt-secondary">Изменения состава на согласовании у Центрики</p>
          ) : null}
          {/* Заявка на обучение у менеджера (training-order-filling): клиенту — что дальше. */}
          {order.dataStatus === 'filling' ? (
            <p className="mt-1 text-12 text-amber">
              Заявка у менеджера — заполните данные слушателей и подтвердите их
            </p>
          ) : null}
          {/* «Заполнено N из M» (training-listener-completeness) — у заказа обучения в фазе данных. */}
          {order.service === 'training' &&
          actions.canContinue &&
          (order.listenersTotal ?? 0) > 0 ? (
            <p
              className={cn(
                'mt-1 text-12',
                order.listenersComplete === order.listenersTotal ? 'text-green' : 'text-amber',
              )}
            >
              Заполнено {order.listenersComplete ?? 0} из {order.listenersTotal}
            </p>
          ) : null}
          {hasIncompleteTrainingConsents(order) ? (
            <p className="mt-1 flex items-center gap-1.5 text-12 text-amber">
              <AlertTriangle className="size-4 shrink-0 stroke-current" aria-hidden />
              {trainingConsentCounterLabel(order.consentsUploaded, order.consentsTotal)}
            </p>
          ) : null}
          {/* Текущий этап отдельной строкой, под ним — строка дат заказа (centrika-docs!95):
              «Создан <дата>» и, если заказ отправляли, «· Отправлен <дата>» (сохранённая дата
              отправки из `submittedAt`; у неотправленного заказа строка содержит только дату
              создания). */}
          <p className="mt-1 text-12 text-txt-tertiary">Этап: {order.currentStage.label}</p>
          <p className="mt-0.5 text-12 text-txt-tertiary">
            Создан {formatDate(order.createdAt)}
            {order.submittedAt ? ` · Отправлен ${formatDate(order.submittedAt)}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {actions.canContinue ? (
            <Button size="sm" onClick={onOpen}>
              Продолжить
            </Button>
          ) : (
            <Button size="sm" variant="secondary" onClick={onOpen}>
              Открыть
            </Button>
          )}
          {actions.canDownload ? (
            <Button size="sm" variant="secondary" disabled={busy} onClick={onDownload}>
              Скачать
            </Button>
          ) : null}
          <Button size="sm" variant="secondary" disabled={busy} onClick={onExport}>
            Выгрузить в Excel
          </Button>
          {actions.canDelete ? (
            <Button size="sm" variant="secondary" disabled={busy} onClick={onDelete}>
              Удалить
            </Button>
          ) : null}
          {order.canRestore ? (
            <Button size="sm" variant="secondary" disabled={busy} onClick={onRestore}>
              Восстановить
            </Button>
          ) : null}
          {order.canCancel ? (
            <Button size="sm" variant="secondary" disabled={busy} onClick={onCancel}>
              Отменить заказ
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  )
}

export function OrdersPage() {
  const navigate = useNavigate()
  const { context, refresh } = useAuth()
  const individual = context?.user.role === 'individual'
  const services = servicesForRole(individual)
  // Фильтры — в адресе страницы (orders-list-filters): ссылку можно сохранить, «Назад» вернёт их.
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = readOrderListFilters(searchParams, services)
  const filterService = filters.service
  const filterStatus = filters.status
  const filterStage = filters.stage
  const [items, setItems] = useState<OrderCard[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  // Фильтры сменились не через селекты (ссылка, пункт меню «Мои заказы») — тоже на первую страницу,
  // без запроса за старой страницей.
  const filterKey = `${filterService ?? ''}|${filterStatus ?? ''}|${filterStage ?? ''}`
  const [pageFilterKey, setPageFilterKey] = useState(filterKey)
  if (pageFilterKey !== filterKey) {
    setPageFilterKey(filterKey)
    setPage(1)
  }
  // Счётчик перезагрузки страницы после действия над заказом (удаление черновика,
  // issue #14 п.6): список и `total` обязаны перечитываться с сервера — локальная
  // правка массива оставила бы страницу неполной, а счётчик страниц завышенным.
  const [reload, setReload] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  // Заказ, который клиент отменяет или восстанавливает (order-cancel-restore): окно подтверждения.
  // Заказ остаётся в состоянии и после закрытия окна — заголовок не мигает на анимации закрытия.
  const [cancelling, setCancelling] = useState<OrderCard | null>(null)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [restoring, setRestoring] = useState<OrderCard | null>(null)
  const [restoreOpen, setRestoreOpen] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    // Индикатор у пункта «Мои заказы» обязан соответствовать актуальным статусам при открытии
    // страницы (centrika-docs!98) — перечитываем контекст кабинета вместе со списком.
    void refresh()
    ordersApi
      .list(
        {
          ...orderListQuery(
            { service: filterService, status: filterStatus, stage: filterStage },
            servicesForRole(individual),
          ),
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов (удалён последний черновик страницы) —
        // возвращаемся на первую.
        if (res.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить заказы'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [refresh, page, reload, individual, filterService, filterStatus, filterStage])

  // Смена фильтров открывает первую страницу — в том же обработчике, без лишнего запроса.
  function changeFilters(next: OrderListFilterValue) {
    setPage(1)
    setSearchParams(orderListFilterParams(next), { replace: true })
  }

  async function onDelete(id: string) {
    setActionError(null)
    setBusyId(id)
    try {
      await ordersApi.remove(id)
      setReload((n) => n + 1)
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось удалить черновик'))
    } finally {
      setBusyId(null)
    }
  }

  // Отмена и восстановление меняют статус и счётчик у «Мои заказы» — список перечитывается вместе с
  // контекстом кабинета (эффект списка вызывает refresh). Ошибка остаётся в окне подтверждения, а
  // список перечитывается и при ней: заказ могли отменить или завершить с другой стороны.
  async function onCancel(order: OrderCard, reason: string) {
    try {
      await ordersApi.cancel(order.id, reason)
      setCancelOpen(false)
    } finally {
      setReload((n) => n + 1)
    }
  }

  async function onRestore(order: OrderCard) {
    try {
      await ordersApi.restore(order.id)
      setRestoreOpen(false)
    } finally {
      setReload((n) => n + 1)
    }
  }

  async function onExport(id: string) {
    setActionError(null)
    setBusyId(id)
    try {
      const order = await ordersApi.get(id)
      exportOrderToExcel(order)
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось выгрузить заказ'))
    } finally {
      setBusyId(null)
    }
  }

  async function onDownload(id: string) {
    setActionError(null)
    setBusyId(id)
    try {
      const order = await ordersApi.get(id)
      if (order.documents.length === 0) {
        setActionError('Готовых файлов по заказу пока нет')
        return
      }
      for (const doc of order.documents) {
        downloadFromUrl(doc.downloadUrl, doc.title)
      }
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось скачать файлы'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <PageHead
        title="Мои заказы"
        subtitle="Заказы вашей организации по услугам Центрики"
        actions={
          <Button
            leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
            onClick={() => setShowForm((s) => !s)}
          >
            Новый заказ
          </Button>
        }
      />

      {showForm ? (
        <CreateOrderForm
          individual={individual}
          onCreated={(id, service) => navigate(serviceAnketaPath(id, service))}
        />
      ) : null}

      {actionError ? (
        <Alert variant="danger" className="mb-4">
          {actionError}
        </Alert>
      ) : null}

      <OrderListFilters services={services} value={filters} onChange={changeFilters} />

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : items.length === 0 ? (
        <Panel>
          {hasOrderListFilters(filters) ? (
            <EmptyState
              icon={ClipboardList}
              title="Заказов по выбранным фильтрам нет"
              description="Измените фильтры или сбросьте их"
              action={
                <Button variant="secondary" onClick={() => changeFilters({})}>
                  Сбросить фильтры
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={ClipboardList}
              title="Заказов пока нет"
              description="Создайте первый заказ — выберите услугу и укажите объём"
            />
          )}
        </Panel>
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((o) => (
            <OrderCardItem
              key={o.id}
              order={o}
              busy={busyId === o.id}
              onOpen={() => navigate(clientOrderTarget(o))}
              onDelete={() => void onDelete(o.id)}
              onExport={() => void onExport(o.id)}
              onDownload={() => void onDownload(o.id)}
              onCancel={() => {
                setCancelling(o)
                setCancelOpen(true)
              }}
              onRestore={() => {
                setRestoring(o)
                setRestoreOpen(true)
              }}
            />
          ))}
        </div>
      )}
      <CancelOrderDialog
        open={cancelOpen}
        side="client"
        orderNumber={cancelling?.number ?? ''}
        onClose={() => setCancelOpen(false)}
        onConfirm={(reason) => (cancelling ? onCancel(cancelling, reason) : Promise.resolve())}
      />
      <RestoreOrderDialog
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        onConfirm={() => (restoring ? onRestore(restoring) : Promise.resolve())}
      />
      <Pagination page={page} totalPages={pageCount(total)} onChange={setPage} />
    </div>
  )
}
